import {
    DataTable as PrimeDataTable,
    type DataTableFilterMeta,
    type DataTablePageEvent,
    type DataTableSortEvent,
} from 'primereact/datatable';
import { Column, type ColumnProps } from 'primereact/column';
import { Paginator } from '@/shared/ui/Paginator/Paginator';
import { RowContextMenu } from '@/shared/ui/RowContextMenu/RowContextMenu';
import type { ActionMenuItem } from '@/shared/ui/ActionMenu/types';
import { toast } from 'sonner';
import { Button } from 'primereact/button';
import classes from './DataTable.module.scss';
import { useNavigate } from 'react-router-dom';
import {
    useLayoutEffect,
    useRef,
    useState,
    type SyntheticEvent,
} from 'react';
import { DateTimeFilter } from '@/shared/ui/DateTimeFilter/DateTimeFilter';
import { dateTimeFilterToBounds, type DateTimeFilterBounds } from '@/shared/lib/dateTimeFilter';
import { getDataTableFilterValue } from '@/shared/lib/dataTableFilters';
import { matchesDateTimeFilter, withoutDateTimeFilters } from '@/shared/lib/calendarColumnFilters';
import { RequestEmpty } from '@/shared/ui/RequestState';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';

/** Описывает колонку для конкретного типа строки */
export interface ColumnConfig<TRow extends object> {
    field: string;
    header: React.ReactNode;
    sortable?: boolean;
    clickable?: boolean;
    filter?: boolean;
    filterPlaceholder?: string;
    style?: React.CSSProperties;
    headerStyle?: React.CSSProperties;
    alignHeader?: ColumnProps['alignHeader'];
    bodyClassName?: ColumnProps['bodyClassName'];
    body?: (rowData: TRow) => React.ReactNode;
    dataType?: 'date' | 'text' | 'number' | 'boolean';
    dateFilterField?: string;
    showFilterMenu?: boolean;
    showClearButton?: boolean;
    sortFunction?: ColumnProps['sortFunction'];
    filterField?: string;
    filterElement?: ColumnProps['filterElement'];
    filterMenuStyle?: React.CSSProperties;
}

export interface DataTableProps<TRow extends object = object> {
    title?: string | undefined;
    embedded?: boolean;
    className?: string;
    removableSort?: boolean;
    stripedRows?: boolean;
    data: TRow[];
    dataKey?: string;
    columns: ColumnConfig<TRow>[];
    loading?: boolean;
    onRowClick?: (data: TRow) => void;
    navigatePath?: string;
    getRowUrl?: (row: TRow) => string | undefined;
    rowActions?: (row: TRow) => ActionMenuItem[] | Promise<ActionMenuItem[]>;
    totalRecords?: number;
    rowsPerPageOptions?: number[];
    globalFilterFields?: string[];
    emptyMessage?: string;
    size?: 'small' | 'normal' | 'large';
    selectionMode?: 'single' | undefined;
    header?: React.ReactNode;
    actionButtons?: (rowData: TRow) => React.ReactNode;

    // Пагинация
    paginator?: boolean;
    lazy?: boolean;
    onPageChange?: (e: DataTablePageEvent) => void;
    first?: number;
    rows?: number;
    currentPageReportTemplate?: string,

    // Фильтрация
    filters?: DataTableFilterMeta;
    onFilterChange?: (filters: DataTableFilterMeta) => void;
    onResetFilters?: () => void;

    /** Передаёт серверному списку календарные границы колонки */
    onDateFilterChange?: (field: string, bounds: DateTimeFilterBounds | null) => void;
    filterDisplay?: 'menu' | 'row';

    // Сортировка
    onSort?: (e: DataTableSortEvent) => void;
    sortField?: string;
    sortOrder?: number;
}

/** Отображает данные в таблице с действиями и фильтрами. */
export const DataTable = <TRow extends object,>(props: DataTableProps<TRow>) => {
    const {
        selectionMode,
        embedded = false,
        className = '',
        title,
        removableSort = true,
        stripedRows = true,
        data,
        dataKey = 'id',
        columns,
        loading = false,
        onRowClick,
        navigatePath,
        totalRecords = 0,
        rowsPerPageOptions = [5, 10, 25, 50],
        globalFilterFields = [],
        emptyMessage = "Данные не найдены.",
        size = 'small',
        header,
        paginator = false,
        lazy = false,
        first = 0,
        rows = 10,

        onPageChange,
        filters,
        onFilterChange = undefined,
        onResetFilters,

        onSort,
        sortField,
        sortOrder,

        filterDisplay = 'row'
    } = props;

    const navigate = useNavigate();
    const [dateFilters, setDateFilters] = useState<Record<string, string[] | null>>({});
    const [internalFirst, setInternalFirst] = useState(0);
    const [internalRows, setInternalRows] = useState(rows);
    const scrollRef = useRef<HTMLDivElement>(null);
    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    useLayoutEffect(() => {
        const element = scrollRef.current;

        if (!element) return;

        /** Ограничивает таблицу доступной высотой окна */
        const updateHeight = () => {
            const top = Math.max(0, element.getBoundingClientRect().top);

            const availableHeight = top < window.innerHeight / 2
                ? window.innerHeight - top - 16
                : window.innerHeight * 0.65;

            const height = `${Math.max(200, availableHeight)}px`;

            if (element.style.getPropertyValue('--vox-table-viewport-height') !== height) {
                element.style.setProperty('--vox-table-viewport-height', height);
            }
        };

        updateHeight();
        window.addEventListener('resize', updateHeight);

        const observer = typeof ResizeObserver === 'undefined'
            ? null
            : new ResizeObserver(updateHeight);

        observer?.observe(element);

        if (element.parentElement) observer?.observe(element.parentElement);

        return () => {
            window.removeEventListener('resize', updateHeight);
            observer?.disconnect();
        };
    }, []);

    const tableFirst = onPageChange
        ? first
        : internalFirst;

    const tableRows = onPageChange
        ? rows
        : internalRows;

    const dateColumns = columns.filter(column => column.dataType === 'date');
    const dateFields = dateColumns.map(column => column.field);

    /** Возвращает применённое календарное условие колонки */
    const getDateFilter = (field: string): unknown => onFilterChange && filters
        ? getDataTableFilterValue(filters, field)
        : dateFilters[field];

    /** Применяет календарь и возвращает список на первую страницу */
    const changeDateFilter = (field: string, value: string[] | null) => {
        setDateFilters(current => ({
            ...current,
            [field]: value
        }));

        setInternalFirst(0);

        const next: DataTableFilterMeta = {
            ...filtersRef.current,
            [field]: {
                value,
                matchMode: 'custom'
            },
        };

        filtersRef.current = next;
        onFilterChange?.(next);

        props.onDateFilterChange?.(field, dateTimeFilterToBounds(value));

        if (!onFilterChange) onPageChange?.({
            first: 0,
            rows: tableRows,
            page: 0,
            pageCount: Math.ceil(totalRecords / tableRows)
        });
    };

    /** Сохраняет календарные условия при изменении обычного фильтра */
    const changeFilters = (next: DataTableFilterMeta) => {
        const savedDates = Object.fromEntries(
            Object.entries(filtersRef.current ?? {}).filter(([field]) => dateFields.includes(field)),
        );

        const combined = {
            ...next,
            ...savedDates
        };

        filtersRef.current = combined;
        onFilterChange?.(combined);
    };

    /** Показывает общий календарь в заголовке временной колонки */
    const renderHeader = (column: ColumnConfig<TRow>) => column.dataType === 'date'
        && typeof column.header === 'string'
        ? <DateTimeFilter
            label={column.header}
            value={getDateFilter(column.field)}
            onChange={value => changeDateFilter(column.field, value)}
        />
        : column.header;


    /** Показывает кнопки действий выбранной строки */
    const actionColumnBody = (
        row: TRow,
        show: (event: SyntheticEvent, row: TRow) => void,
    ) => (
        <div className={classes.rowActions}>
            {props.actionButtons?.(row)}
            {(props.rowActions || getRowId(row) || getRowUrl(row) || onRowClick) && (
                <ActionButton
                    type="button"
                    icon="pi pi-ellipsis-h"
                    iconOnly
                    className={classes.rowMenuButton}
                    text
                    rounded
                    aria-label="Действия со строкой"
                    aria-haspopup="menu"
                    onClick={(event) => show(event, row)}
                    onKeyDown={(event) => {
                        if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) show(event, row);
                    }}
                />
            )}
        </div>
    );

    /** Передаёт выбранную страницу таблицы */
    const handlePageChange = (e: DataTablePageEvent) => {
        if (onPageChange) {
            onPageChange(e);
        } else {
            setInternalFirst(e.first);
            setInternalRows(e.rows);
        }
    };

    /** Возвращает идентификатор выбранной записи */
    const getRowId = (row: unknown): string | undefined => {
        if (typeof row !== 'object' || row === null) return undefined;

        const id = Reflect.get(row, dataKey);

        return typeof id === 'string' || typeof id === 'number'
            ? String(id)
            : undefined;
    };

    /** Возвращает адрес карточки выбранной записи */
    const getRowUrl = (row: TRow): string | undefined => {
        if (props.getRowUrl) return props.getRowUrl(row);

        const id = getRowId(row);

        return navigatePath && id
            ? `${navigatePath}/${encodeURIComponent(id)}`
            : undefined;
    };

    /** Собирает доступные действия выбранной записи */
    const getContextMenuItems = async (row: TRow): Promise<ActionMenuItem[]> => {
        const url = getRowUrl(row);
        const id = getRowId(row);
        const items: ActionMenuItem[] = [];

        if (url || onRowClick) {
            items.push({
                label: 'Открыть',
                icon: 'pi pi-external-link',
                command: () => {
                    if (url) navigate(url);
                    else onRowClick?.(row);
                },
            });
        }

        if (url) {
            items.push({
                label: 'Открыть в новой вкладке',
                icon: 'pi pi-window-maximize',
                command: () => window.open(url, '_blank', 'noopener,noreferrer'),
            });
        }

        if (id) {
            items.push({
                label: 'Скопировать ID',
                icon: 'pi pi-copy',
                command: async () => {
                    try {
                        await navigator.clipboard.writeText(id);
                        toast.success('ID скопирован');
                    } catch {
                        toast.error('Не удалось скопировать ID');
                    }
                },
            });
        }

        const actions = await props.rowActions?.(row) ?? [];

        return [...items, ...(items.length && actions.length
            ? [{ separator: true }]
            : []), ...actions];
    };

    /** Открывает запись по нажатию на ячейку */
    const handleCellClick = (rowData: TRow, column: ColumnConfig<TRow>) => {
        if (!column.clickable) return;

        if (onRowClick) {
            onRowClick(rowData);
        } else if (navigatePath && getRowId(rowData)) {
            navigate(`${navigatePath}/${getRowId(rowData)}`);
        }
    };

    /** Открывает выбранную строку таблицы */
    const handleRowClick = (e: { originalEvent: React.MouseEvent<HTMLElement>; data: unknown }) => {
        const clickedAction = e.originalEvent?.target
            && (e.originalEvent.target as HTMLElement).closest('.action-button, button, .p-button');

        if (clickedAction) {
            return;
        }

        if (e.originalEvent && e.data) {
            if (onRowClick) {
                onRowClick(e.data as TRow);
            } else if (navigatePath && getRowId(e.data)) {
                navigate(`${navigatePath}/${getRowId(e.data)}`);
            }
        }
    };

    /** Читает значение вложенного поля строки */
    const getNestedValue = (obj: unknown, path: string): React.ReactNode => {
        const value = path.split('.')
            .reduce<unknown>((current, key) =>
                current !== null && typeof current === 'object' && key in current
                    ? Reflect.get(current, key)
                    : undefined, obj);

        return value as React.ReactNode;
    };

    const visibleData = lazy
        ? data
        : data.filter(row => dateColumns.every(column => matchesDateTimeFilter(
            getNestedValue(row, column.dateFilterField ?? column.field),
            getDateFilter(column.field),
        )));

    const empty = !loading && visibleData.length === 0;

    const tableContent = (
        <RowContextMenu getItems={getContextMenuItems}>
            {(showMenu) => (
                <div className={onRowClick && !columns.some(c => c.clickable)
                    ? classes.rowsClickable
                    : undefined
                }>
                    {title && (
                        <h2>
                            {title}
                        </h2>
                    )}
                    <div
                        ref={scrollRef}
                        className={`${classes.scrollableWrapper} ${empty
                            ? classes.emptyTable
                            : ''}`}
                    >
                        <PrimeDataTable
                            value={visibleData as Array<Record<string, unknown>>}
                            scrollable={false}
                            selectionMode={selectionMode}
                            loading={loading}
                            stripedRows={stripedRows}
                            removableSort={removableSort}
                            size={size}
                            totalRecords={totalRecords}
                            rowsPerPageOptions={rowsPerPageOptions}
                            onRowClick={columns.some(c => c.clickable)
                                ? undefined
                                : handleRowClick
                            }
                            paginator={paginator && (!empty || lazy && totalRecords > 0)}
                            pt={{ paginator: { root: { className: classes.paginatorHost } } }}
                            rows={tableRows}
                            first={tableFirst}
                            onPage={handlePageChange}
                            onFilter={(event) => changeFilters(event.filters)}
                            dataKey={dataKey}
                            filters={filters
                                ? withoutDateTimeFilters(filters, dateFields)
                                : undefined
                            }
                            onContextMenu={(event) => showMenu(event.originalEvent, event.data as TRow)}

                            {...(onSort
                                ? { onSort }
                                : {})}
                            {...(sortField !== undefined
                                ? { sortField }
                                : {})}
                            {...(sortOrder === 1 || sortOrder === 0 || sortOrder === -1
                                ? { sortOrder }
                                : {})}

                            filterDisplay={filterDisplay}
                            globalFilterFields={globalFilterFields}
                            header={onResetFilters
                                ? <div className="flex align-items-center justify-content-between flex-wrap gap-2">
                                    {header &&
                                        <div className="flex-1">
                                            {header}
                                        </div>
                                    }
                                    <Button
                                        type="button"
                                        label="Сбросить все"
                                        icon="pi pi-filter-slash"
                                        outlined
                                        size="small"
                                        onClick={onResetFilters}
                                    />
                                </div>
                                : header
                            }
                            emptyMessage={loading
                                ? 'Загрузка данных...'
                                : <RequestEmpty message={emptyMessage} />
                            }
                            lazy={lazy}
                            paginatorTemplate={{
                                layout: 'CurrentPageReport',
                                CurrentPageReport: report => <Paginator
                                    first={tableFirst}
                                    rows={tableRows}
                                    totalRecords={report.totalRecords}
                                    rowsPerPageOptions={rowsPerPageOptions}
                                    disabled={loading}
                                    label={title
                                        ? `Страницы: ${title}`
                                        : 'Страницы таблицы'
                                    }
                                    onPageChange={handlePageChange}
                                />
                            }}
                        >
                            {columns.map((column, index) => (
                                <Column
                                    key={index}
                                    field={column.field}
                                    header={renderHeader(column)}
                                    sortable={column.sortable}
                                    filter={column.dataType === 'date'
                                        ? false
                                        : column.filter
                                    }
                                    alignHeader={column.alignHeader ?? 'center'}
                                    filterPlaceholder={column.filterPlaceholder ?? (typeof column.header === 'string'
                                        ? `Поиск: ${column.header}`
                                        : undefined
                                    )}
                                    pt={{
                                        filterInput: {
                                            role: 'group',
                                            'aria-label': typeof column.header === 'string'
                                                ? `Фильтр: ${column.header}`
                                                : undefined
                                    }}}
                                    style={column.style}
                                    headerStyle={column.headerStyle}
                                    bodyClassName={column.bodyClassName}
                                    body={(rowData: TRow) => {
                                        const content = column.body
                                            ? column.body(rowData)
                                            : getNestedValue(rowData, column.field);

                                        if (!column.clickable) return content;

                                        return (
                                            <button
                                                type="button"
                                                className={classes.clickableCell}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCellClick(rowData, column);
                                                }}
                                            >
                                                {content}
                                            </button>
                                        );
                                    }}
                                    dataType={column.dataType}
                                    showFilterMenu={column.showFilterMenu ?? false}
                                    showClearButton={column.showClearButton ?? false}
                                    sortFunction={column.sortFunction}
                                    filterField={column.filterField}
                                    filterElement={column.filterElement}
                                />
                            ))}

                            {(props.actionButtons || props.rowActions || props.getRowUrl || navigatePath || onRowClick
                                || data.some(row => getRowId(row) !== undefined)) && (
                                    <Column
                                        key="actions"
                                        header={null}
                                        alignHeader={'center'}
                                        className={`${classes.actionColumn} ${props.actionButtons
                                            ? classes.expandedActions
                                            : ''}`}
                                        headerClassName={`${classes.actionColumn} ${props.actionButtons
                                            ? classes.expandedActions
                                            : ''}`}
                                        body={(row: TRow) => actionColumnBody(row, showMenu)}
                                    />
                                )}
                        </PrimeDataTable>
                    </div>
                </div>
            )}
        </RowContextMenu>
    );

    if (embedded) {
        return tableContent;
    }

    return <div className={`card ${classes.tableCard} ${empty
        ? classes.emptyTable
        : ''} ${className}`}>
        {tableContent}
    </div>;
};
